import React, { useRef, useCallback, useState } from "react";
import { SubmissionReviewCard } from "components/SubmissionReviewCard";
import ReviewBonusSubmissionInput, {
  ReviewBonusSubmissionInputRef,
  ReviewBonusSubmissionInputValues,
} from "components/ReviewBonusSubmissionInput";
import ReviewARSubmissionInput, {
  ReviewARSubmissionInputRef,
  ReviewARSubmissionInputValues,
} from "components/ReviewARSubmissionInput";
import { getARRejectionConfig } from "components/ReviewARSubmissionInput/constants";
import { formatCurrency } from "@wellth/utils";
import { RejectionReasonsOptions } from "./constants";
import { ConfirmRejectionModal } from "./ConfirmRejectionModal";

export interface BonusBehaviorSubmissionRecord {
  id: string;
  name: string;
  status: string;
  timestamp: string;
  shortId: string;
  firstName: string;
  lastName: string;
  programCode: string;
  rewardAmount: number;
  rewardCurrency: string;
  photoUrl: string;
  photoUrls?: Array<string | null> | null;
  // From `BonusBehaviorSubmissionItem.verification` (BE enum
  // `ActionRewardVerificationType`). When the value isn't one we recognize,
  // the legacy single-select reject UI renders (CR fallback).
  verification?: string | null;
}

export interface ReviewCardProps extends BonusBehaviorSubmissionRecord {
  submit: (review: ReviewBonusSubmissionInputValues) => Promise<void>;
  submitAR?: (review: ReviewARSubmissionInputValues) => Promise<void>;
  onError: (error: Error) => void;
  routeToMemberPage: () => void;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  submit,
  submitAR,
  id,
  name,
  shortId,
  timestamp,
  firstName,
  lastName,
  programCode,
  rewardAmount,
  rewardCurrency,
  photoUrl,
  photoUrls,
  onError,
  routeToMemberPage,
  verification,
}) => {
  const [submitEnabled, setSubmitEnabled] = useState(true);
  const legacyRef = useRef<ReviewBonusSubmissionInputRef>(null);
  const arRef = useRef<ReviewARSubmissionInputRef>(null);

  const [arValid, setArValid] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [
    pendingReview,
    setPendingReview,
  ] = useState<ReviewARSubmissionInputValues | null>(null);

  const config = getARRejectionConfig(name, verification);
  const isAR = config !== null;

  const photoUrlsArray = React.useMemo(
    () => {
      if (!photoUrls || photoUrls.length === 0) {
        return [];
      }
      return photoUrls.filter((url): url is string => url !== null);
    },
    [photoUrls],
  );

  const onSubmitLegacy = useCallback(
    () => {
      const submitForm = async (): Promise<void> => {
        setSubmitEnabled(false);
        try {
          const reviewValues = legacyRef.current.getSubmissionReviewInput();
          await submit(reviewValues);
        } catch (error) {
          setSubmitEnabled(true);
          onError(error);
        }
      };
      void submitForm();
    },
    [onError, submit],
  );

  const submitARValues = useCallback(
    async (values: ReviewARSubmissionInputValues): Promise<void> => {
      if (!submitAR) {
        onError(new Error("AR submit handler is not wired"));
        return;
      }
      setSubmitEnabled(false);
      try {
        await submitAR(values);
      } catch (error) {
        setSubmitEnabled(true);
        onError(error);
      }
    },
    [onError, submitAR],
  );

  const onSubmitAR = useCallback(
    () => {
      const reviewValues = arRef.current.getSubmissionReviewInput();
      if (reviewValues.approved) {
        void submitARValues(reviewValues);
        return;
      }
      setPendingReview(reviewValues);
      setConfirmOpen(true);
    },
    [submitARValues],
  );

  const handleConfirmReject = useCallback(
    () => {
      if (!pendingReview) return;
      setConfirmOpen(false);
      void submitARValues(pendingReview);
      setPendingReview(null);
    },
    [pendingReview, submitARValues],
  );

  const handleCancelConfirm = useCallback(() => {
    setConfirmOpen(false);
    setPendingReview(null);
  }, []);

  const footerButtons = isAR
    ? [
        {
          key: "submitButton",
          title: "Submit",
          disabled: !submitEnabled || !arValid || !submitAR,
          type: "primary" as const,
          onClick: onSubmitAR,
        },
      ]
    : [
        {
          key: "submitButton",
          title: "Submit",
          disabled: !submitEnabled,
          onClick: onSubmitLegacy,
        },
      ];

  return (
    <>
      <SubmissionReviewCard
        key={`${id}-${shortId}`}
        title={name}
        footerButtons={footerButtons}
        details={{
          submittedOn: new Date(timestamp),
          submittedBy: `${firstName} ${lastName} (${shortId})`,
          programCode,
          reward: formatCurrency(rewardAmount, rewardCurrency),
          submissionId: id,
          routeToMemberPage,
        }}
        photoUrl={photoUrl}
        photoUrls={photoUrlsArray}
      >
        {config !== null ? (
          <ReviewARSubmissionInput
            ref={arRef}
            config={config}
            onValidityChange={setArValid}
          />
        ) : (
          <ReviewBonusSubmissionInput
            ref={legacyRef}
            reasons={RejectionReasonsOptions}
          />
        )}
      </SubmissionReviewCard>
      {pendingReview && (
        <ConfirmRejectionModal
          visible={confirmOpen}
          arLabel={name}
          review={pendingReview}
          onConfirm={handleConfirmReject}
          onCancel={handleCancelConfirm}
        />
      )}
    </>
  );
};

export default ReviewCard;
